import {
  ArrowUpRight,
  Ban,
  CheckCircle2,
  ChevronRight,
  Hourglass,
  Link2,
  Trash2,
} from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

import { getCurrentRoleId, getIsSuperUser } from 'common-src/features/auth';
import { getDashboardClients, getRoleById } from 'common-src/models/Client';
import { getDashboardV2Appointments } from 'common-src/models/DashboardV2Appointment';
import { getDashboardV2Tasks } from 'common-src/models/DashboardV2Task';
import { isFeatureEnabled } from 'common-src/models/FeatureFlag';
import { getTaskReasonById } from 'common-src/models/PatientTask';

import useCustomSelector from 'src/hooks/useCustomSelector';

import {
  BLOCKED_REASON_LABELS,
  BLOCKED_REASON_OPTIONS,
  BLOCKED_STATUS,
  ESCALATED_TO_OPTIONS,
  EVIDENCE_TYPES,
  MANUAL_TASK_CANCEL_FEATURE_FLAG,
  pathwayLabel,
  PRIORITY_BADGE_COPY,
  PRIORITY_SORT_ORDER,
  TASK_DISPLAY_STATUS,
  teamMeta,
} from '../../constants';
import {
  buildLinkedTaskStructure,
  canMarkTaskComplete,
  formatClinicianName,
  formatDue,
  getClientByAthenaProviderId,
  getDelegatedSubtaskMeta,
  getLinkedTaskWaitingForTitle,
  getPatient,
  getTaskAssessmentTypeLabel,
  getTaskAssessmentVariantLabel,
  getTaskCancelDecisionPayload,
  getTaskDecisionOptions,
  getTaskDisplayStatus,
  getTaskDisplayStatusLabel,
  getTaskDisplayTitle,
  isAssessmentTask,
  isAutomaticWorkflowTask,
  isLinkedTask,
  isMessageTask,
  isTaskCanceled,
  isTaskCaregiverAssisted,
  isTaskEscalatedStatus,
  isTaskReadinessComplete,
  isTaskUnableToComplete,
  isWorkflowBlockedTask,
} from '../../helpers';
import AthenaLink from '../AthenaLink';
import { Badge } from '../Badge';
import CompleteWithSubtasksModal from '../CompleteWithSubtasksModal';
import DelegateSubtasksModal from '../DelegateSubtasksModal';
import EscalateTaskModal from '../EscalateTaskModal';
import PlatformLink from '../PlatformLink';
import ReassignModal from '../ReassignModal';
import TaskConversation from '../TaskConversation';
import styles from './TaskDetail.module.scss';

const isTaskFollowedByCurrentUser = (task, currentUserId) => {
  const normalizedCurrentUserId = `${currentUserId ?? ''}`.trim();
  if (!normalizedCurrentUserId) return false;

  return (task?.followedBy || []).some(
    (followerId) => `${followerId ?? ''}`.trim() === normalizedCurrentUserId,
  );
};

const TaskDetail = ({
  task,
  cachedDelegatedSubtasks,
  onClose,
  onBackToReadiness,
  readinessAppointmentLabel,
  readinessStatus,
  onAssignToMe,
  isAssigning,
  onUnassign,
  isUnassigning,
  onMarkComplete,
  isMarking,
  onCancelTask,
  isCanceling,
  onReassign,
  isReassigning,
  onFollowTask,
  isFollowing,
  onUnfollowTask,
  isUnfollowing,
  onBlockTask,
  isBlocking,
  onUnblockTask,
  isUnblocking,
  onEscalateTask,
  isEscalating,
  onDeescalateTask,
  isDeescalating,
  onDelegateSubtasks,
  isDelegatingSubtasks,
  onCancelSubtask,
  cancelingSubtaskId,
  currentUserId,
  currentUserName,
  onReplySent,
  onSelectTask,
}) => {
  const [showReassignModal, setShowReassignModal] = React.useState(false);
  const [showDelegateSubtasksModal, setShowDelegateSubtasksModal] = React.useState(false);
  const [showEscalateModal, setShowEscalateModal] = React.useState(false);
  const [showCompleteWithSubtasksModal, setShowCompleteWithSubtasksModal] = React.useState(false);
  const [pendingCompletionPayload, setPendingCompletionPayload] = React.useState(null);
  const [showCancelReason, setShowCancelReason] = React.useState(false);
  const [cancelReason, setCancelReason] = React.useState('');
  const [showBlockDropdown, setShowBlockDropdown] = React.useState(false);
  const [linkedTasksExpanded, setLinkedTasksExpanded] = React.useState(true);
  const [currentOwner, setCurrentOwner] = React.useState(task?.owner || '');
  const [wasJustClaimed, setWasJustClaimed] = React.useState(false);
  const [isFollowed, setIsFollowed] = React.useState(
    isTaskFollowedByCurrentUser(task, currentUserId),
  );
  const [loadedDelegatedSubtasks, setLoadedDelegatedSubtasks] = React.useState([]);
  const blockDropdownRef = React.useRef(null);

  if (!task) return null;

  const patient = task?.patient ?? getPatient(task?.patientId);
  const team = teamMeta[task.team];
  const clients = useCustomSelector((state) => getDashboardClients(state)) || [];
  const appointments = useCustomSelector(getDashboardV2Appointments) || [];
  const allTasks = useCustomSelector(getDashboardV2Tasks) || [];
  const canUseManualTaskCancel = useCustomSelector((state) =>
    isFeatureEnabled(state, MANUAL_TASK_CANCEL_FEATURE_FLAG),
  );
  const canUseAdminOnlyActions = useCustomSelector(
    (state) => getCurrentRoleId(state) === 1 || getIsSuperUser(state),
  );
  const due = task?.due ? formatDue(task.due) : { text: '—', urgent: false };
  const dueDateText = task?.due ? String(task.due).split('T')[0] : '—';
  const evidenceType = EVIDENCE_TYPES[task?.evidence?.type];
  const pathway = pathwayLabel[task.pathway] ?? task.pathway;
  const normalizedCurrentOwner = `${currentOwner || ''}`.trim().toLowerCase();
  const isUnassigned = !normalizedCurrentOwner || normalizedCurrentOwner === 'unassigned';
  const isClaimedByYou =
    !!`${currentUserName || ''}`.trim() &&
    `${currentOwner || ''}`.trim().toLowerCase() ===
      `${currentUserName || ''}`.trim().toLowerCase();
  const assignedClientRole = React.useMemo(() => {
    const owner = `${currentOwner || ''}`.trim().toLowerCase();
    if (!owner) return '';

    const matchingClient = clients.find((client) => {
      const fullName =
        typeof client?.getName === 'function'
          ? client.getName(false, true)
          : `${client?.firstName || ''} ${client?.lastName || ''}`.trim();
      const basicName = `${client?.firstName || ''} ${client?.lastName || ''}`.trim();

      return fullName.toLowerCase() === owner || basicName.toLowerCase() === owner;
    });

    if (!matchingClient) return '';
    return (
      matchingClient?.role || (matchingClient?.roleId ? getRoleById(matchingClient.roleId) : '')
    );
  }, [clients, currentOwner]);
  const getClientNameById = React.useCallback(
    (clientId) => {
      const normalizedClientId = Number(clientId);
      if (!Number.isInteger(normalizedClientId)) return null;

      const client = clients.find((candidateClient) => candidateClient?.id === normalizedClientId);
      if (!client) return null;

      if (typeof client.getName === 'function') {
        return client.getName(false, true);
      }

      return `${client?.firstName || ''} ${client?.lastName || ''}`.trim() || null;
    },
    [clients],
  );
  const assignedTo = isUnassigned ? 'Unassigned' : `${currentOwner} (${assignedClientRole || '—'})`;
  const parentTask = React.useMemo(
    () =>
      allTasks.find(
        (candidateTask) =>
          `${candidateTask?.id || ''}`.trim() === `${task?.parentTaskId || ''}`.trim(),
      ) || null,
    [allTasks, task?.parentTaskId],
  );
  const parentTaskTitle = parentTask ? getTaskDisplayTitle(parentTask) : '';
  const parentTaskSummary = `${
    parentTask?.taskDescription || parentTask?.task_description || parentTask?.caption || ''
  }`.trim();
  const parentTaskOwner = `${parentTask?.owner || ''}`.trim() || 'Unassigned';
  const parentTaskOwnerInitials = parentTaskOwner
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
  const parentTaskTeam = parentTask ? teamMeta[parentTask?.team] : null;
  const parentTaskDueDateText = parentTask?.due ? String(parentTask.due).split('T')[0] : '';
  const reasonLabel = getTaskReasonById(task?.reasonId)?.label || '-';
  const createdByLabel = task?.workflowInstanceId
    ? 'Workflow (system)'
    : getClientNameById(task?.createdBy) || '-';
  const linkedAppointment = appointments.find(
    (appointment) => `${appointment?.id || ''}`.trim() === `${task?.externalObjectId || ''}`.trim(),
  );
  const clinicianClient = getClientByAthenaProviderId(clients, linkedAppointment?.providerAthenaId);
  const clinician = formatClinicianName(clinicianClient);
  const taskIsMessage = isMessageTask(task);
  const taskIsAssessment = isAssessmentTask(task);
  const isAutomaticTask = isAutomaticWorkflowTask(task);
  const taskTitle = getTaskDisplayTitle(task);
  const taskDescription = `${task?.taskDescription || task?.task_description || ''}`.trim();
  const assessmentTypeLabel = getTaskAssessmentTypeLabel(task);
  const assessmentVariantLabel = getTaskAssessmentVariantLabel(task);
  const caregiverAssisted = isTaskCaregiverAssisted(task);
  const taskSop = task?.taskSop ?? task?.task_sop ?? '';
  const displayStatusLabel = getTaskDisplayStatusLabel(task);
  const taskDecisionOptions = getTaskDecisionOptions(task);
  const hasDecisionActions = taskDecisionOptions.length > 0;
  const hasTaskSop = !!`${taskSop}`.trim();
  const assessmentReviewLink = task?.noteUrl ?? '';
  const hasAssessmentReviewLink = !!`${assessmentReviewLink}`.trim();
  const isCompletedTask = isTaskReadinessComplete(task);
  const isCanceledTask = isTaskCanceled(task);
  const isUnableToCompleteTask = isTaskUnableToComplete(task);
  const isBlockedStatus = task?.blockedStatus === BLOCKED_STATUS.Blocked;
  const isEscalatedStatus = isTaskEscalatedStatus(task);
  const blockedReasonLabel =
    task?.blockedReason != null ? (BLOCKED_REASON_LABELS[task.blockedReason] ?? 'Blocked') : null;
  const blockedByLabel = getClientNameById(task?.blockedBy);
  const blockedAtLabel = task?.blockedAt
    ? new Date(task.blockedAt).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;
  const escalatedByLabel = getClientNameById(task?.escalatedBy);
  const escalatedToUserLabel = getClientNameById(task?.escalatedToUser);
  const escalatedToOption = ESCALATED_TO_OPTIONS.find(
    (option) => option.value === Number(task?.escalatedTo),
  );

  const escalatedToLabel =
    escalatedToUserLabel || (escalatedToOption ? `${escalatedToOption.label} team` : null);

  const escalatedAtLabel = task?.escalatedAt
    ? new Date(task.escalatedAt).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;
  const isPrerequisiteBlockedTask = isLinkedTask(task);
  const isBlockedByDependencies = isWorkflowBlockedTask(task);
  const taskPriority = task?.priority;
  React.useEffect(() => {
    if (task?.parentTaskId || !task?.id) {
      setLoadedDelegatedSubtasks([]);
      return undefined;
    }

    setLoadedDelegatedSubtasks(
      Array.isArray(cachedDelegatedSubtasks) ? cachedDelegatedSubtasks : [],
    );
    return undefined;
  }, [cachedDelegatedSubtasks, task?.id, task?.parentTaskId]);

  const delegatedSubtasks = React.useMemo(() => {
    const parentTaskId = `${task?.id || ''}`.trim();
    if (!parentTaskId) return [];

    const mergedSubtasksById = new Map();

    loadedDelegatedSubtasks.forEach((subtask) => {
      const subtaskId = `${subtask?.id || ''}`.trim();
      if (!subtaskId) return;
      mergedSubtasksById.set(subtaskId, subtask);
    });

    allTasks.forEach((candidateTask) => {
      if (`${candidateTask?.parentTaskId || ''}` !== parentTaskId) return;

      const subtaskId = `${candidateTask?.id || ''}`.trim();
      if (!subtaskId) return;
      mergedSubtasksById.set(subtaskId, candidateTask);
    });

    return Array.from(mergedSubtasksById.values()).filter(
      (candidateTask) => `${candidateTask?.parentTaskId || ''}` === parentTaskId,
    );
  }, [allTasks, loadedDelegatedSubtasks, task?.id]);
  const delegatedSubtaskMeta = React.useMemo(
    () => getDelegatedSubtaskMeta(delegatedSubtasks),
    [delegatedSubtasks],
  );
  const openDelegatedSubtasks = delegatedSubtaskMeta.openSubtasks;
  const completedDelegatedSubtasksCount = delegatedSubtaskMeta.completedCount ?? 0;
  const canceledDelegatedSubtasksCount = delegatedSubtaskMeta.canceledCount ?? 0;
  const doneDelegatedSubtasksCount = delegatedSubtaskMeta.doneCount;
  const isWaitingOnSubtasks = !task?.parentTaskId && delegatedSubtaskMeta.waitingOnSubtasks;
  const areAllDelegatedSubtasksComplete = !task?.parentTaskId && delegatedSubtaskMeta.allDone;
  const canDelegateMoreSubtasks = isClaimedByYou && !task?.parentTaskId && !isAutomaticTask;
  const sortedDelegatedSubtasks = React.useMemo(
    () =>
      [...delegatedSubtasks].sort((taskA, taskB) => {
        const statusA = getTaskDisplayStatus(taskA) === TASK_DISPLAY_STATUS.Done ? 1 : 0;
        const statusB = getTaskDisplayStatus(taskB) === TASK_DISPLAY_STATUS.Done ? 1 : 0;

        if (statusA !== statusB) return statusA - statusB;

        return (
          new Date(taskA?.due || taskA?.taskAssignDueAt || 0) -
          new Date(taskB?.due || taskB?.taskAssignDueAt || 0)
        );
      }),
    [delegatedSubtasks],
  );
  const prerequisiteTaskTitle = React.useMemo(() => {
    if (!isPrerequisiteBlockedTask) return null;

    const sortedSiblingTasks = [...allTasks].sort((taskA, taskB) => {
      const priorityA = PRIORITY_SORT_ORDER[taskA?.priority] ?? Number.MAX_SAFE_INTEGER;
      const priorityB = PRIORITY_SORT_ORDER[taskB?.priority] ?? Number.MAX_SAFE_INTEGER;
      if (priorityA !== priorityB) return priorityA - priorityB;
      return new Date(taskA?.due || 0) - new Date(taskB?.due || 0);
    });

    return getLinkedTaskWaitingForTitle(task, sortedSiblingTasks);
  }, [allTasks, isPrerequisiteBlockedTask, task?.externalObjectId, task?.id]);
  const linkedChildTasks = React.useMemo(() => {
    const taskId = `${task?.id || ''}`;
    if (!taskId || task?.parentTaskId) return [];
    const { linkedChildTasksByParentId } = buildLinkedTaskStructure(allTasks);
    return linkedChildTasksByParentId.get(taskId) || [];
  }, [allTasks, task?.id, task?.parentTaskId]);

  const showMarkComplete =
    !isCompletedTask && isClaimedByYou && !taskIsMessage && canMarkTaskComplete(task);
  const showCancelTask =
    canUseManualTaskCancel &&
    canUseAdminOnlyActions &&
    isClaimedByYou &&
    !taskIsMessage &&
    !isAutomaticTask;
  const showCompletionActions = showMarkComplete && !showCancelReason;

  React.useEffect(() => {
    setCurrentOwner(task?.owner || '');
    setWasJustClaimed(false);
    setIsFollowed(isTaskFollowedByCurrentUser(task, currentUserId));
    setShowBlockDropdown(false);
    setShowEscalateModal(false);
    setShowCompleteWithSubtasksModal(false);
    setPendingCompletionPayload(null);
    setShowCancelReason(false);
    setCancelReason('');
  }, [currentUserId, task?.followedBy, task?.id]);

  React.useEffect(() => {
    setCurrentOwner(task?.owner || '');
  }, [task?.owner]);

  React.useEffect(() => {
    if (!showBlockDropdown) return undefined;

    const handleClickOutside = (event) => {
      if (blockDropdownRef.current?.contains(event.target)) return;
      setShowBlockDropdown(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showBlockDropdown]);

  const handleClaimTask = async () => {
    if (!task?.id) return;
    const wasAssigned = (await onAssignToMe?.(task.id)) ?? false;
    if (!wasAssigned) return;
    setCurrentOwner(currentUserName || currentOwner);
    setWasJustClaimed(true);
  };

  const handleFollowTask = async () => {
    if (!task?.id || isFollowing) return;
    const wasFollowed = (await onFollowTask?.(task.id)) ?? false;
    if (!wasFollowed) return;
    setIsFollowed(true);
  };

  const handleMarkCompleteClick = async (completionPayload = null) => {
    if (!task?.id || isMarking) return;

    if (!task?.parentTaskId && openDelegatedSubtasks.length > 0) {
      setPendingCompletionPayload(completionPayload);
      setShowCompleteWithSubtasksModal(true);
      return;
    }

    await onMarkComplete?.(task.id, completionPayload ?? undefined);
  };

  const handleCancelTaskClick = async () => {
    if (!task?.id || isCanceling) return;
    const normalizedReason = cancelReason.trim();
    if (!showCancelReason) {
      setShowCancelReason(true);
      return;
    }
    if (!normalizedReason) return;

    const wasCanceled =
      (await onCancelTask?.(task.id, normalizedReason, getTaskCancelDecisionPayload(task))) ??
      false;
    if (!wasCanceled) return;
    setCancelReason('');
    setShowCancelReason(false);
  };

  const handleUnfollowTask = async () => {
    if (!task?.id || isUnfollowing) return;
    const wasUnfollowed = (await onUnfollowTask?.(task.id)) ?? false;
    if (!wasUnfollowed) return;
    setIsFollowed(false);
  };

  const handleOpenAssessmentReview = () => {
    if (!hasAssessmentReviewLink) return;
    window.open(assessmentReviewLink, '_blank', 'noopener,noreferrer');
  };

  const handleBlockedButtonClick = () => {
    if (isUnblocking) return;
    if (isBlockedStatus) {
      onUnblockTask?.(task.id);
      return;
    }
    if (isBlocking || isEscalating) return;
    setShowBlockDropdown((prev) => !prev);
  };

  const handleBlockReasonSelect = async (blockedReason) => {
    const wasBlocked = (await onBlockTask?.(task.id, blockedReason)) ?? false;
    if (!wasBlocked) return;
    setShowBlockDropdown(false);
  };

  const handleEscalateButtonClick = () => {
    if (isEscalating || isDeescalating || isBlocking || isUnblocking) return;
    if (isEscalatedStatus) {
      onDeescalateTask?.(task.id);
      return;
    }
    setShowBlockDropdown(false);
    setShowEscalateModal(true);
  };

  const systemBanner = !isCompletedTask && isAutomaticTask && (
    <div className={styles.systemBanner}>
      <div className={styles.systemBannerCopy}>
        <div className={styles.systemLabel}>Handled by system</div>
        <div className={styles.systemText}>
          This automatic workflow step updates itself when the ZUS job changes state.
        </div>
      </div>
      {task?.callbackStatus ? <Badge variant="outline">{task.callbackStatus}</Badge> : null}
    </div>
  );

  const followingBanner = !isAutomaticTask && !isClaimedByYou && isFollowed && (
    <div className={styles.followingBanner}>
      <div className={styles.followingLabel}>You are following this task</div>
      <div className={styles.followingBannerActions}>
        {isUnassigned && (
          <button
            type="button"
            className={styles.claimButton}
            disabled={isAssigning}
            onClick={handleClaimTask}
          >
            {isAssigning ? 'Claiming...' : 'Claim'}
          </button>
        )}
        <button
          type="button"
          className={styles.unfollowButton}
          disabled={isUnfollowing}
          onClick={handleUnfollowTask}
        >
          {isUnfollowing ? 'Unfollowing...' : 'Unfollow'}
        </button>
      </div>
    </div>
  );

  const claimBanner = !isAutomaticTask && isUnassigned && !isFollowed && (
    <div className={styles.unclaimedBanner}>
      <div className={styles.unclaimedBannerCopy}>
        <div className={styles.unclaimedLabel}>Unclaimed</div>
        <div className={styles.unclaimedText}>
          Claim to start working &middot; others will see it&apos;s taken
        </div>
      </div>
      <div className={styles.unclaimedBannerActions}>
        <button
          type="button"
          className={styles.claimButton}
          disabled={isAssigning}
          onClick={handleClaimTask}
        >
          {isAssigning ? 'Claiming...' : 'Claim'}
        </button>
        <button
          type="button"
          className={styles.followButton}
          disabled={isFollowing}
          onClick={handleFollowTask}
        >
          {isFollowing ? 'Following...' : 'Follow'}
        </button>
      </div>
    </div>
  );

  const followBanner = !isAutomaticTask && !isUnassigned && !isClaimedByYou && !isFollowed && (
    <div className={styles.unclaimedBanner}>
      <div className={styles.unclaimedBannerCopy}>
        <div className={styles.unclaimedLabel}>Follow this task</div>
        <div className={styles.unclaimedText}>Stay updated while someone else owns it</div>
      </div>
      <div className={styles.unclaimedBannerActions}>
        <button
          type="button"
          className={styles.followButton}
          disabled={isFollowing}
          onClick={handleFollowTask}
        >
          {isFollowing ? 'Following...' : 'Follow'}
        </button>
      </div>
    </div>
  );

  const claimedBanner = !isAutomaticTask && isClaimedByYou && !isUnassigned && (
    <div className={styles.claimedBanner}>
      <div className={styles.claimedBannerCopy}>
        <div className={styles.claimedLabel}>
          <span className={styles.claimedDot} aria-hidden="true" />
          Claimed by you
        </div>
      </div>
      <div className={styles.claimedTime}>{wasJustClaimed ? 'just now' : ''}</div>
    </div>
  );
  const waitingOnSubtasksBanner = !isCompletedTask && isWaitingOnSubtasks && (
    <div className={styles.waitingOnSubtasksBanner}>
      <div className={styles.waitingOnSubtasksIconWrap} aria-hidden="true">
        <Hourglass className={styles.waitingOnSubtasksIcon} />
      </div>
      <div className={styles.waitingOnSubtasksCopy}>
        <div className={styles.waitingOnSubtasksLabel}>
          {`Waiting on ${openDelegatedSubtasks.length} sub-task${
            openDelegatedSubtasks.length === 1 ? '' : 's'
          }`}
        </div>
        <div className={styles.waitingOnSubtasksText}>
          {`${doneDelegatedSubtasksCount} of ${delegatedSubtasks.length} complete · you'll be notified when all are done.`}
          {canceledDelegatedSubtasksCount > 0
            ? ` ${completedDelegatedSubtasksCount} completed, ${canceledDelegatedSubtasksCount} canceled.`
            : ''}
        </div>
      </div>
    </div>
  );
  const allSubtasksCompletedBanner = !isCompletedTask && areAllDelegatedSubtasksComplete && (
    <div className={styles.allSubtasksCompletedBanner}>
      <div className={styles.allSubtasksCompletedIconWrap} aria-hidden="true">
        <CheckCircle2 className={styles.allSubtasksCompletedIcon} />
      </div>
      <div className={styles.allSubtasksCompletedCopy}>
        <div className={styles.allSubtasksCompletedLabel}>No active sub-tasks remaining</div>
        <div className={styles.allSubtasksCompletedText}>
          {canceledDelegatedSubtasksCount > 0
            ? `${completedDelegatedSubtasksCount} completed, ${canceledDelegatedSubtasksCount} canceled · ready for processing.`
            : 'Ready for processing · complete this task to finish.'}
        </div>
      </div>
    </div>
  );
  const linkedTasksSection = linkedChildTasks.length > 0 && (
    <div className={styles.linkedTasksSection}>
      <button
        type="button"
        className={styles.linkedTasksHeader}
        onClick={() => setLinkedTasksExpanded((prev) => !prev)}
      >
        <Link2 className={styles.linkedTasksHeaderIcon} aria-hidden="true" />
        <span className={styles.linkedTasksHeaderLabel}>
          Linked tasks ({linkedChildTasks.length})
        </span>
        <ChevronRight
          className={`${styles.linkedTasksHeaderChevron} ${
            linkedTasksExpanded ? styles.linkedTasksHeaderChevronExpanded : ''
          }`}
          aria-hidden="true"
        />
      </button>
      {linkedTasksExpanded && (
        <div className={styles.linkedTasksList}>
          {linkedChildTasks.map((linkedTask) => {
            const linkedTaskTitle = getTaskDisplayTitle(linkedTask);
            const linkedTaskDisplayStatus = getTaskDisplayStatus(linkedTask);
            const linkedTaskOwner = `${linkedTask?.owner || ''}`.trim() || 'Unassigned';
            const linkedTaskDue = formatDue(linkedTask?.due || linkedTask?.taskAssignDueAt);
            return (
              <div
                key={linkedTask.id}
                role="button"
                tabIndex={0}
                className={styles.linkedTaskRow}
                onClick={() => onSelectTask?.(linkedTask)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  onSelectTask?.(linkedTask);
                }}
              >
                <span className={styles.linkedTaskTitle}>{linkedTaskTitle}</span>
                <span className={styles.linkedTaskMeta}>
                  <Badge>{linkedTaskDisplayStatus}</Badge>
                  <span className={styles.linkedTaskOwner}>{linkedTaskOwner}</span>
                  <span className={styles.linkedTaskDue}>{linkedTaskDue.text}</span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const subtasksSection = delegatedSubtasks.length > 0 && (
    <div className={styles.subtasksSection}>
      <div className={styles.subtasksHeader}>
        <div className={styles.sectionLabel}>
          {`Sub-tasks · ${openDelegatedSubtasks.length} active`}
          {completedDelegatedSubtasksCount > 0
            ? ` · ${completedDelegatedSubtasksCount} completed`
            : ''}
          {canceledDelegatedSubtasksCount > 0
            ? ` · ${canceledDelegatedSubtasksCount} canceled`
            : ''}
        </div>
        {canDelegateMoreSubtasks && (
          <button
            type="button"
            className={styles.subtasksHeaderAction}
            onClick={() => setShowDelegateSubtasksModal(true)}
          >
            + Delegate more
          </button>
        )}
      </div>
      <div className={styles.subtasksList}>
        {sortedDelegatedSubtasks.map((subtask) => {
          const subtaskTitle = getTaskDisplayTitle(subtask);
          const subtaskDisplayStatus = getTaskDisplayStatus(subtask);
          const isCompletedSubtask = subtaskDisplayStatus === TASK_DISPLAY_STATUS.Done;
          const isCanceledSubtask = isTaskCanceled(subtask);
          const isCancelingSubtask = `${cancelingSubtaskId ?? ''}` === `${subtask.id ?? ''}`;
          const subtaskDue = formatDue(subtask?.due || subtask?.taskAssignDueAt);
          const subtaskTeam = teamMeta[subtask?.team];
          const subtaskTeamLabel = subtaskTeam ? `${subtaskTeam.label} team` : 'Assigned directly';
          const canCancelSubtask =
            canDelegateMoreSubtasks && !isCompletedSubtask && !isCancelingSubtask;

          return (
            <div
              key={subtask.id}
              role="button"
              tabIndex={0}
              className={`${styles.subtaskRow} ${
                isCanceledSubtask
                  ? styles.subtaskRowCanceled
                  : isCompletedSubtask
                    ? styles.subtaskRowCompleted
                    : ''
              }`}
              onClick={() => onSelectTask?.(subtask)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                event.preventDefault();
                onSelectTask?.(subtask);
              }}
            >
              <span
                className={`${styles.subtaskStatusDot} ${
                  isCanceledSubtask
                    ? styles.subtaskStatusDotCanceled
                    : isCompletedSubtask
                      ? styles.subtaskStatusDotCompleted
                      : ''
                }`}
                aria-hidden="true"
              />
              <span className={styles.subtaskRowPrimary}>
                <span
                  className={`${styles.subtaskTitle} ${
                    isCanceledSubtask
                      ? styles.subtaskTitleCanceled
                      : isCompletedSubtask
                        ? styles.subtaskTitleCompleted
                        : ''
                  }`}
                >
                  {subtaskTitle}
                </span>
              </span>
              <span className={styles.subtaskRowMeta}>
                <span className={styles.subtaskTeamWrap}>
                  {subtaskTeam ? (
                    <span className={styles.subtaskTeamIcon} aria-hidden="true">
                      {subtaskTeam.icon}
                    </span>
                  ) : null}
                  <span className={styles.subtaskMeta}>{subtaskTeamLabel}</span>
                </span>
              </span>
              <span
                className={`${styles.subtaskDue} ${
                  subtaskDue.urgent && !isCompletedSubtask ? styles.subtaskDueUrgent : ''
                }`}
              >
                {subtaskDue.text}
              </span>
              <button
                type="button"
                className={`${styles.subtaskTrashButton} ${
                  !canCancelSubtask ? styles.subtaskTrashButtonDisabled : ''
                }`}
                disabled={!canCancelSubtask}
                onClick={async (event) => {
                  event.stopPropagation();
                  await onCancelSubtask?.(subtask.id);
                }}
                aria-label={isCancelingSubtask ? 'Canceling sub-task' : 'Cancel sub-task'}
              >
                <Trash2 size={12} aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
  const blockedByPrerequisiteBanner =
    isPrerequisiteBlockedTask && !isCompletedTask ? (
      <div className={styles.blockedBanner}>
        <div className={styles.blockedBannerIconWrap} aria-hidden="true">
          <Link2 className={styles.blockedBannerIcon} />
        </div>
        <div className={styles.blockedBannerCopy}>
          <div className={styles.blockedLabel}>
            {isBlockedByDependencies ? 'Blocked by prerequisites' : 'Blocked by prerequisite'}
          </div>
          {prerequisiteTaskTitle ? (
            <div className={styles.blockedWaitingText}>
              <span className={styles.blockedWaitingLabel}>
                {isBlockedByDependencies ? 'Blocked by:' : 'Waiting on:'}
              </span>{' '}
              {prerequisiteTaskTitle}
            </div>
          ) : null}
        </div>
      </div>
    ) : null;
  const completedBanner = isCompletedTask && (
    <div
      className={
        isCanceledTask
          ? styles.canceledBanner
          : isUnableToCompleteTask
            ? styles.unableToCompleteBanner
            : styles.completedBanner
      }
    >
      <div
        className={
          isCanceledTask
            ? styles.canceledLabel
            : isUnableToCompleteTask
              ? styles.unableToCompleteLabel
              : styles.completedLabel
        }
      >
        <span
          className={
            isCanceledTask
              ? styles.canceledMark
              : isUnableToCompleteTask
                ? styles.unableToCompleteMark
                : styles.completedCheck
          }
          aria-hidden="true"
        >
          {isCanceledTask ? <Trash2 size={12} strokeWidth={2} /> : '✓'}
        </span>
        {isCanceledTask ? 'Canceled' : isUnableToCompleteTask ? 'Unable to Complete' : 'Completed'}
      </div>
    </div>
  );
  const blockedStatusBanner =
    !isCompletedTask && isBlockedStatus ? (
      <div className={styles.blockedStatusBanner} role="status">
        <div className={styles.blockedStatusIconWrap} aria-hidden="true">
          <Ban className={styles.blockedStatusIcon} />
        </div>
        <div className={styles.blockedStatusCopy}>
          <div className={styles.blockedStatusTitle}>
            Blocked
            {blockedReasonLabel ? ` · ${blockedReasonLabel}` : ''}
          </div>
          {blockedByLabel || blockedAtLabel ? (
            <div className={styles.blockedStatusMeta}>
              {blockedByLabel ? `Set by ${blockedByLabel}` : null}
              {blockedByLabel && blockedAtLabel ? ' · ' : null}
              {blockedAtLabel}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          className={styles.blockedStatusAction}
          disabled={isUnblocking}
          onClick={() => onUnblockTask?.(task.id)}
        >
          {isUnblocking ? 'Unblocking...' : 'Unblock'}
        </button>
      </div>
    ) : null;
  const escalatedStatusBanner =
    !isCompletedTask && isEscalatedStatus ? (
      <div className={styles.escalatedStatusBanner} role="status">
        <div className={styles.escalatedStatusIconWrap} aria-hidden="true">
          <ArrowUpRight className={styles.escalatedStatusIcon} />
        </div>
        <div className={styles.escalatedStatusCopy}>
          <div className={styles.escalatedStatusTitle}>
            Escalated
            {escalatedToLabel ? ` → ${escalatedToLabel}` : ''}
          </div>
          {task?.escalatedReason || escalatedByLabel || escalatedAtLabel ? (
            <div className={styles.escalatedStatusMeta}>
              {task?.escalatedReason ? <span>{task.escalatedReason}</span> : null}
              {task?.escalatedReason && (escalatedByLabel || escalatedAtLabel) ? ' · ' : null}
              {escalatedByLabel ? `Set by ${escalatedByLabel}` : null}
              {escalatedByLabel && escalatedAtLabel ? ' · ' : null}
              {escalatedAtLabel}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          className={styles.escalatedStatusAction}
          disabled={isDeescalating}
          onClick={() => onDeescalateTask?.(task.id)}
        >
          {isDeescalating ? 'De-escalating...' : 'De-escalate'}
        </button>
      </div>
    ) : null;

  const readinessBadgeLabel =
    readinessStatus === 'green'
      ? 'Ready'
      : readinessStatus === 'yellow'
        ? 'At risk'
        : readinessStatus === 'red'
          ? 'Not ready'
          : null;

  return (
    <>
      {onBackToReadiness && readinessAppointmentLabel && (
        <button type="button" className={styles.readinessBanner} onClick={onBackToReadiness}>
          <span className={styles.readinessBannerArrow} aria-hidden="true">
            ←
          </span>
          <span className={styles.readinessBannerBody}>
            <span className={styles.readinessBannerLabel}>Back to readiness</span>
            <span className={styles.readinessBannerTitle}>{readinessAppointmentLabel}</span>
          </span>
          {readinessBadgeLabel ? (
            <Badge variant={readinessStatus}>{readinessBadgeLabel}</Badge>
          ) : null}
        </button>
      )}
      <div className={styles.root}>
        {isCompletedTask ? completedBanner : claimedBanner}
        {systemBanner}
        {allSubtasksCompletedBanner}
        {waitingOnSubtasksBanner}
        {!isCompletedTask && followingBanner}
        {blockedStatusBanner}
        {escalatedStatusBanner}
        {!isCompletedTask && blockedByPrerequisiteBanner}

        <div className={styles.sectionHeader}>
          <div className={styles.headerTop}>
            <div className={styles.title}>{taskTitle}</div>
            <button type="button" onClick={onClose} className={styles.closeButton}>
              ×
            </button>
          </div>
          {taskDescription ? <div className={styles.taskDescription}>{taskDescription}</div> : null}
          {task.caption && <div className={styles.caption}>{task.caption}</div>}
          <div className={styles.badgesRow}>
            {!isCompletedTask ? (
              isWaitingOnSubtasks ? (
                <Badge className={styles.waitingStatusBadge}>Waiting on sub-tasks</Badge>
              ) : areAllDelegatedSubtasksComplete ? (
                <Badge variant="green">Ready for processing</Badge>
              ) : (
                <Badge variant={isUnableToCompleteTask ? 'orange' : 'default'}>
                  {displayStatusLabel}
                </Badge>
              )
            ) : null}
            {pathway ? <Badge variant="outline">{pathway}</Badge> : null}
            <span
              className={`${styles.priorityBadge} ${styles[`priorityBadge${taskPriority}`] || ''}`}
            >
              {PRIORITY_BADGE_COPY[taskPriority] ?? taskPriority}
            </span>
            {task.mustHave2026 && <Badge variant="edge">2026 Target</Badge>}
          </div>
        </div>

        <div className={styles.sectionInfo}>
          <div className={styles.infoGrid}>
            {[
              ['Patient', `${patient?.name} · ${patient?.mrn}`],
              ['Assigned to', assignedTo],
              ['Team', team ? `${team.icon} ${team.label}` : '—'],
              ['Due', [dueDateText, due.text].filter(Boolean).join('  ')],
              ['Clinician', clinician],
            ].map(([key, value], index) => (
              <div key={index} className={styles.infoRow}>
                <span className={styles.infoLabel}>{key}</span>
                <span className={styles.infoValue}>{value}</span>
              </div>
            ))}
          </div>
          {(patient || task?.patientId) && (
            <div className={styles.athenaLinkWrap}>
              <PlatformLink patient={patient} patientId={task?.patientId} />
            </div>
          )}
          {patient && (
            <div className={styles.athenaLinkWrap}>
              <AthenaLink
                patient={patient}
                patientId={task?.patientId}
                integration={patient?.athenaIntegration ?? null}
              />
            </div>
          )}
        </div>

        {task?.parentTaskId && parentTask ? (
          <div className={styles.parentTaskSection}>
            <button
              type="button"
              className={styles.parentTaskCard}
              onClick={() => onSelectTask?.(parentTask)}
            >
              <span className={styles.parentTaskIconWrap} aria-hidden="true">
                <Hourglass className={styles.parentTaskIcon} />
              </span>
              <span className={styles.parentTaskCopy}>
                <span className={styles.parentTaskTitle}>{`Sub-task of: ${parentTaskTitle}`}</span>
                {parentTaskSummary ? (
                  <span className={styles.parentTaskSummary}>{parentTaskSummary}</span>
                ) : null}
                <span className={styles.parentTaskMeta}>
                  <span className={styles.parentTaskOwnerWrap}>
                    <span className={styles.parentTaskOwnerBadge}>
                      {parentTaskOwnerInitials || '—'}
                    </span>
                    <span>{parentTaskOwner}</span>
                  </span>
                  {parentTaskTeam?.label ? <span>{parentTaskTeam.label}</span> : null}
                  {parentTaskDueDateText ? <span>{`Due ${parentTaskDueDateText}`}</span> : null}
                </span>
              </span>
              <ArrowUpRight
                className={styles.parentTaskArrow}
                strokeWidth={1.8}
                aria-hidden="true"
              />
            </button>
          </div>
        ) : null}

        <div className={styles.sectionEvidence}>
          <div className={styles.sectionLabel}>Why this task exists</div>
          <div className={styles.evidenceMetaGrid}>
            {[
              ['Created by', createdByLabel],
              ['Reason', reasonLabel],
            ].map(([key, value]) => (
              <div key={key} className={styles.infoRow}>
                <span className={styles.infoLabel}>{key}</span>
                <span className={styles.infoValue}>{value}</span>
              </div>
            ))}
          </div>
          <div className={styles.evidenceCard}>
            <div className={styles.evidenceHeader}>
              <span className={styles.evidenceIcon}>{evidenceType?.icon}</span>
              <span className={styles.evidenceLabel}>{evidenceType?.label || 'Evidence'}</span>
            </div>
            <p className={styles.evidenceSummary}>{task?.evidence?.summary || '—'}</p>
            <div className={styles.evidenceSource}>Source: {task?.evidence?.source || '—'}</div>
          </div>
        </div>

        <div className={styles.sopSection}>
          <button
            type="button"
            className={`${styles.sopButton} ${!hasTaskSop ? styles.sopButtonDisabled : ''}`}
            disabled={!hasTaskSop}
            onClick={() => {
              if (!hasTaskSop) return;
              window.open(taskSop, '_blank', 'noopener,noreferrer');
            }}
          >
            <span className={styles.sopButtonIcon} aria-hidden="true">
              📋
            </span>
            <span className={styles.sopButtonLabel}>Read SOP for this task</span>
            <span className={styles.sopButtonArrow} aria-hidden="true">
              ↗
            </span>
          </button>
        </div>

        {linkedTasksSection}

        {subtasksSection}

        {taskIsAssessment && (
          <div className={styles.assessmentSection}>
            <div className={styles.sectionLabel}>Assessment Details</div>
            {(assessmentTypeLabel || assessmentVariantLabel || caregiverAssisted) && (
              <div className={styles.assessmentBadgesRow}>
                {assessmentTypeLabel && (
                  <div className={styles.assessmentTypeBadge}>{assessmentTypeLabel}</div>
                )}
                {assessmentVariantLabel && (
                  <div className={styles.assessmentVariantBadge}>{assessmentVariantLabel}</div>
                )}
                {caregiverAssisted && (
                  <div className={styles.caregiverAssistedBadge}>Caregiver Assisted</div>
                )}
              </div>
            )}
            <button
              type="button"
              className={`${styles.assessmentButton} ${
                !hasAssessmentReviewLink ? styles.assessmentButtonDisabled : ''
              }`}
              disabled={!hasAssessmentReviewLink}
              onClick={handleOpenAssessmentReview}
            >
              <span className={styles.assessmentButtonIcon} aria-hidden="true">
                📊
              </span>
              <span className={styles.assessmentButtonLabel}>Open in Mindy Review</span>
              <span className={styles.assessmentButtonArrow} aria-hidden="true">
                ↗
              </span>
            </button>
          </div>
        )}

        {taskIsMessage && (
          <TaskConversation
            task={task}
            canReply={!isCompletedTask && isClaimedByYou}
            onReplySent={onReplySent}
          />
        )}

        {!isCompletedTask && claimBanner}
        {!isCompletedTask && followBanner}

        {!isCompletedTask && (
          <div className={styles.sectionActions}>
            {(showCompletionActions || showCancelTask) && (
              <div className={styles.primaryActionSection}>
                <div className={styles.primaryActionsRow}>
                  {showCompletionActions &&
                    (taskIsAssessment ? (
                      <>
                        <button
                          type="button"
                          className={styles.primaryAction}
                          disabled={!hasAssessmentReviewLink}
                          onClick={handleOpenAssessmentReview}
                        >
                          Start Review
                        </button>
                        <button
                          type="button"
                          className={styles.assessmentCompleteAction}
                          disabled={isMarking}
                          onClick={() => handleMarkCompleteClick()}
                        >
                          {isMarking ? 'Marking...' : 'Mark Complete'}
                        </button>
                      </>
                    ) : hasDecisionActions ? (
                      taskDecisionOptions.map((decisionOption) => (
                        <button
                          key={decisionOption.value}
                          type="button"
                          className={styles.primaryAction}
                          disabled={isMarking}
                          onClick={() =>
                            handleMarkCompleteClick({
                              result: decisionOption.value,
                              decision: decisionOption.value,
                            })
                          }
                        >
                          {isMarking ? 'Marking...' : decisionOption.label}
                        </button>
                      ))
                    ) : (
                      <button
                        type="button"
                        className={styles.primaryAction}
                        disabled={isMarking}
                        onClick={() => handleMarkCompleteClick()}
                      >
                        {isMarking ? 'Marking...' : 'Mark Complete'}
                      </button>
                    ))}
                  {showCancelTask && !showCancelReason && (
                    <button
                      type="button"
                      className={`${styles.secondaryAction} ${styles.cancelTaskAction}`}
                      disabled={isCanceling}
                      onClick={handleCancelTaskClick}
                    >
                      {isCanceling ? 'Canceling...' : 'Cancel Task'}
                    </button>
                  )}
                </div>
                {showCancelReason && (
                  <div className={styles.cancelReasonPanel}>
                    <textarea
                      className={styles.cancelReasonInput}
                      value={cancelReason}
                      onChange={(event) => setCancelReason(event.target.value)}
                      placeholder="Reason for canceling this task"
                      rows={3}
                    />
                    <div className={styles.cancelActionRow}>
                      <button
                        type="button"
                        className={styles.secondaryAction}
                        disabled={isCanceling}
                        onClick={() => {
                          setShowCancelReason(false);
                          setCancelReason('');
                        }}
                      >
                        Keep Task
                      </button>
                      <button
                        type="button"
                        className={`${styles.secondaryAction} ${styles.cancelTaskAction}`}
                        disabled={isCanceling || !cancelReason.trim()}
                        onClick={handleCancelTaskClick}
                      >
                        {isCanceling ? 'Canceling...' : 'Confirm Cancel'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {!isAutomaticTask && (
              <div
                className={`${styles.secondaryActionSection} ${
                  taskIsMessage || isUnassigned || !showCompletionActions
                    ? styles.secondaryActionSectionStandalone
                    : ''
                }`}
              >
                <div
                  className={`${styles.secondaryActionsRow} ${
                    isClaimedByYou ? styles.secondaryActionsRowClaimed : ''
                  }`}
                >
                  <div className={styles.secondaryActionsGroup}>
                    {!isUnassigned && !isClaimedByYou && (
                      <button
                        type="button"
                        className={styles.secondaryAction}
                        onClick={() => setShowReassignModal(true)}
                      >
                        Reassign
                      </button>
                    )}
                    {isClaimedByYou && (
                      <button
                        type="button"
                        className={styles.secondaryAction}
                        disabled={isUnassigning}
                        onClick={async () => {
                          if (!task?.id) return;
                          const wasUnassigned = (await onUnassign?.(task.id)) ?? false;
                          if (!wasUnassigned) return;
                          setCurrentOwner('Unassigned');
                          setWasJustClaimed(false);
                        }}
                      >
                        {isUnassigning ? 'Unclaiming...' : 'Unclaim'}
                      </button>
                    )}
                    {(isUnassigned || isClaimedByYou) && (
                      <button
                        type="button"
                        className={styles.secondaryAction}
                        onClick={() => setShowReassignModal(true)}
                      >
                        Assign to...
                      </button>
                    )}
                    {isClaimedByYou && !task?.parentTaskId && (
                      <button
                        type="button"
                        className={styles.secondaryAction}
                        onClick={() => setShowDelegateSubtasksModal(true)}
                      >
                        Delegate sub-tasks...
                      </button>
                    )}
                  </div>
                  <div
                    className={`${styles.secondaryActionsGroup} ${
                      isClaimedByYou ? styles.secondaryActionsStatusGroupClaimed : ''
                    }`}
                  >
                    <div className={styles.statusActionWrap} ref={blockDropdownRef}>
                      <button
                        type="button"
                        disabled={isBlocking || isUnblocking}
                        onClick={handleBlockedButtonClick}
                        className={`${styles.statusAction} ${styles.blockedAction} ${
                          isBlockedStatus ? styles.statusActionActive : ''
                        } ${isBlocking || isUnblocking ? styles.statusActionDisabled : ''}`}
                      >
                        <Ban size={14} aria-hidden="true" />
                        {isBlocking
                          ? 'Blocking...'
                          : isUnblocking
                            ? 'Unblocking...'
                            : isBlockedStatus
                              ? 'Unblock'
                              : 'Blocked'}
                      </button>
                      {showBlockDropdown ? (
                        <div className={styles.blockDropdown}>
                          {BLOCKED_REASON_OPTIONS.map((reason) => (
                            <button
                              key={reason.value}
                              type="button"
                              className={styles.blockDropdownItem}
                              onClick={() => handleBlockReasonSelect(reason.value)}
                            >
                              {reason.label}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    <div className={styles.statusActionWrap}>
                      <button
                        type="button"
                        className={`${styles.statusAction} ${styles.escalateAction} ${
                          isEscalatedStatus ? styles.statusActionActive : ''
                        } ${isEscalating || isDeescalating ? styles.statusActionDisabled : ''}`}
                        disabled={isEscalating || isDeescalating}
                        onClick={handleEscalateButtonClick}
                      >
                        <ArrowUpRight size={14} aria-hidden="true" />
                        {isDeescalating
                          ? 'De-escalating...'
                          : isEscalating
                            ? 'Escalating...'
                            : isEscalatedStatus
                              ? 'De-escalate'
                              : 'Escalate'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {showReassignModal && (
          <ReassignModal
            task={{ ...task, owner: currentOwner }}
            onClose={() => setShowReassignModal(false)}
            isSubmitting={isReassigning}
            currentUserName={currentUserName}
            onConfirm={async (client) => {
              if (!client?.id || !task?.id) return false;

              const wasReassigned = (await onReassign?.(task.id, client.id)) ?? true;
              if (!wasReassigned) return false;

              const name =
                typeof client?.getName === 'function'
                  ? client.getName(false, true)
                  : `${client?.firstName || ''} ${client?.lastName || ''}`.trim();

              if (name) setCurrentOwner(name);
              return true;
            }}
          />
        )}
        {showDelegateSubtasksModal && (
          <DelegateSubtasksModal
            task={task}
            onClose={() => setShowDelegateSubtasksModal(false)}
            currentUserName={currentUserName}
            isSubmitting={isDelegatingSubtasks}
            onConfirm={async (rows) => (await onDelegateSubtasks?.(task, rows)) ?? false}
          />
        )}
        {showEscalateModal && (
          <EscalateTaskModal
            task={task}
            onClose={() => setShowEscalateModal(false)}
            isSubmitting={isEscalating}
            currentUserName={currentUserName}
            onConfirm={async (escalationPayload) => {
              const wasEscalated = (await onEscalateTask?.(task.id, escalationPayload)) ?? false;

              if (!wasEscalated) return false;
              return true;
            }}
          />
        )}
        {showCompleteWithSubtasksModal && (
          <CompleteWithSubtasksModal
            openCount={openDelegatedSubtasks.length}
            parentTitle={taskTitle}
            isSubmitting={isMarking}
            onClose={() => {
              setPendingCompletionPayload(null);
              setShowCompleteWithSubtasksModal(false);
            }}
            onConfirm={async () => {
              const wasCompleted =
                (await onMarkComplete?.(task.id, pendingCompletionPayload ?? undefined)) ?? false;
              if (wasCompleted) setPendingCompletionPayload(null);
              return wasCompleted;
            }}
          />
        )}
      </div>
    </>
  );
};

TaskDetail.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    owner: PropTypes.string,
    patient: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      mrn: PropTypes.string,
    }),
    patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    team: PropTypes.string,
    due: PropTypes.string,
    createdBy: PropTypes.number,
    taskAssignDueAt: PropTypes.string,
    evidence: PropTypes.shape({
      type: PropTypes.string,
      sourceRef: PropTypes.string,
      summary: PropTypes.string,
      source: PropTypes.string,
    }),
    pathway: PropTypes.string,
    departmentAthenaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    typeId: PropTypes.number,
    reasonId: PropTypes.number,
    reason: PropTypes.string,
    title: PropTypes.string,
    taskDescription: PropTypes.string,
    task_description: PropTypes.string,
    assessmentType: PropTypes.string,
    assessment_type: PropTypes.string,
    assessmentVariant: PropTypes.string,
    assessment_variant: PropTypes.string,
    caregiverAssisted: PropTypes.bool,
    caregiver_assisted: PropTypes.bool,
    caption: PropTypes.string,
    status: PropTypes.number,
    callbackStatus: PropTypes.string,
    blockedStatus: PropTypes.number,
    escalatedStatus: PropTypes.number,
    blockedBy: PropTypes.number,
    blockedAt: PropTypes.string,
    blockedReason: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    escalatedAt: PropTypes.string,
    escalatedBy: PropTypes.number,
    escalatedTo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    escalatedToUser: PropTypes.number,
    escalatedReason: PropTypes.string,
    previousAssigneeId: PropTypes.number,
    unblockedBy: PropTypes.number,
    deescalatedBy: PropTypes.number,
    priority: PropTypes.string,
    isVisible: PropTypes.bool,
    stepType: PropTypes.string,
    dependency: PropTypes.object,
    mustHave2026: PropTypes.bool,
    isFollowing: PropTypes.bool,
    followedBy: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    noteUrl: PropTypes.string,
    taskSop: PropTypes.string,
    task_sop: PropTypes.string,
    threadId: PropTypes.string,
    externalObjectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    parentTaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    workflowInstanceId: PropTypes.string,
  }),
  cachedDelegatedSubtasks: PropTypes.arrayOf(PropTypes.object),
  onClose: PropTypes.func,
  onBackToReadiness: PropTypes.func,
  readinessAppointmentLabel: PropTypes.string,
  readinessStatus: PropTypes.oneOf(['default', 'green', 'yellow', 'red']),
  onAssignToMe: PropTypes.func,
  isAssigning: PropTypes.bool,
  onUnassign: PropTypes.func,
  isUnassigning: PropTypes.bool,
  onMarkComplete: PropTypes.func,
  isMarking: PropTypes.bool,
  onCancelTask: PropTypes.func,
  isCanceling: PropTypes.bool,
  onReassign: PropTypes.func,
  isReassigning: PropTypes.bool,
  onFollowTask: PropTypes.func,
  isFollowing: PropTypes.bool,
  onUnfollowTask: PropTypes.func,
  isUnfollowing: PropTypes.bool,
  onBlockTask: PropTypes.func,
  isBlocking: PropTypes.bool,
  onUnblockTask: PropTypes.func,
  isUnblocking: PropTypes.bool,
  onEscalateTask: PropTypes.func,
  isEscalating: PropTypes.bool,
  onDeescalateTask: PropTypes.func,
  isDeescalating: PropTypes.bool,
  onDelegateSubtasks: PropTypes.func,
  isDelegatingSubtasks: PropTypes.bool,
  onCancelSubtask: PropTypes.func,
  cancelingSubtaskId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentUserName: PropTypes.string,
  onReplySent: PropTypes.func,
  onSelectTask: PropTypes.func,
};

export default TaskDetail;
